import React, { useState } from 'react';
import { apiCall } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { useEnvironmentStore } from '../stores/environmentStore';

export const PartnerTestEvents: React.FC = () => {
  const partnerId = useAuthStore((s) => s.partnerId);
  const mode = useEnvironmentStore((s) => s.mode);
  const isProd = mode === 'production';

  const [eventType, setEventType] = useState('TEST_EVENT');
  const [severity, setSeverity] = useState<'info' | 'warning' | 'critical'>('info');
  const [message, setMessage] = useState('Synthetic test event');
  const [count, setCount] = useState(1);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSend = async () => {
    if (!partnerId) {
      setError('Your account has no partner_id assigned');
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const resp = await apiCall(`/api/config/partners/${encodeURIComponent(partnerId)}/test-events`, {
        method: 'POST',
        body: JSON.stringify({ eventType, severity, message, count }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.error || `Request failed (${resp.status})`);
      }
      setResult(`${data.message} → ${data.synthetic_external_id}`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Test Events</h1>
        <p className="text-sm text-gray-500 mt-1">
          Generate synthetic events for this partner. Use this to verify your downstream flow without
          touching real customer data.
        </p>
      </div>

      {isProd ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 space-y-2">
          <div className="font-semibold">Disabled in production</div>
          <p className="text-sm">
            Test event generation is permanently disabled when the Control Center is running against a production
            Nova environment. Use the test cluster (<code>abena-nova-integration-test</code>) instead.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Event type</label>
              <input
                type="text"
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1D407B]/30 focus:border-[#1D407B]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Severity</label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1D407B]/30 focus:border-[#1D407B]"
              >
                <option value="info">info</option>
                <option value="warning">warning</option>
                <option value="critical">critical</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Message</label>
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1D407B]/30 focus:border-[#1D407B]"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Count (1–100)</label>
            <input
              type="number"
              min={1}
              max={100}
              value={count}
              onChange={(e) => setCount(Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 1)))}
              className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1D407B]/30 focus:border-[#1D407B]"
            />
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSend}
              disabled={busy}
              className="px-4 py-2 bg-[#1D407B] text-white text-sm font-medium rounded-lg hover:bg-[#162f5a] transition-colors disabled:opacity-50"
            >
              {busy ? 'Sending...' : 'Send test events'}
            </button>
          </div>

          {result && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-lg px-4 py-3">
              {result}
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
